import jwt_decode from 'jwt-decode';
import { base64urlEncode, dec2hex, generateHash } from 'shared/utils';
import { createUrl } from 'shared/api/url';
import { getStandEnvironment } from 'shared/utils/stand';
import { AuthConfigInterface } from 'app/services/AuthService/AuthConfigInterface';
import { AuthenticationError, getErrorMessage } from 'shared/errors';
import { configs } from './configs';

export const getAuthStandUrl = () =>
  getStandEnvironment().isProd ? configs.authProdStandUrl : configs.authStageStandUrl;

export const getAuthTokenUrl = () =>
  configs.links.token.replace(':stand', `${getAuthStandUrl()}`);

export const isAuthCompletePage = () => location.href.includes(configs.links.complete);

export const isAccessTokenExpires = (accessToken: string) => {
  try {
    const data = jwt_decode<{ exp: number }>(accessToken);
    const isExpires =
      parseInt(String(Date.now()).slice(0, configs.jwtExpLength), 10) > data.exp;

    return isExpires;
  } catch (error) {
    const pref = 'Check for Access Token Expires: ';
    const errorMessage = getErrorMessage(`${pref}${error}`);

    if (errorMessage) throw new Error(errorMessage);

    throw new AuthenticationError('Catch error while check for access token expires!');
  }
};

export const generateCodeChallengeFromVerifier = async (plain: string) => {
  const hashed = await generateHash(plain, configs.crypto);

  return base64urlEncode(hashed);
};

export const generateCodeVerifier = () => {
  const array = window.crypto.getRandomValues(new Uint32Array(56 / 2));

  return Array.from(array, dec2hex).join('');
};

export const createAuthUrl = (params: {
  redirectUrl: string;
  state: string;
  codeChallenge: string;
  authConfig: AuthConfigInterface;
}) => {
  const authStandUrl = getAuthStandUrl();
  const authUrl = new URL(configs.links.auth.replace(':stand', `${authStandUrl}`));

  authUrl.searchParams.append('client_id', params.authConfig.clientId);
  authUrl.searchParams.append('response_type', configs.responseType);
  authUrl.searchParams.append('scope', configs.scope);
  authUrl.searchParams.append('code_challenge_method', configs.codeChallengeMethod);
  authUrl.searchParams.append('redirect_uri', params.redirectUrl);
  authUrl.searchParams.append('state', params.state);
  authUrl.searchParams.append('code_challenge', params.codeChallenge);

  return authUrl.href;
};

export const getRedirectCompleteUrl = (): string => {
  const { protocol, hostname, port }: Pick<Location, 'protocol' | 'port' | 'hostname'> =
    window.location;
  const url = `${protocol}//${hostname}${port ? ':' + port : ''}${
    configs.links.complete
  }`;

  return createUrl(url, { toString: true });
};

export const getLogoutUrl = (redirectUrl?: string) => {
  const logoutUrl = new URL(
    configs.links.logout.replace(':stand', `${getAuthStandUrl()}`),
  );
  const redirectPath = redirectUrl ?? window.location.origin;

  logoutUrl.searchParams.append('next', redirectPath);

  return logoutUrl.href;
};
